/**
 * Human-readable, metric-aware helper copy for campaign analytics insight cards.
 */

function safeNum(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

export function analyticsPct(count, total) {
  const c = safeNum(count);
  const t = safeNum(total);
  if (t <= 0) return 0;
  return Math.round((c / t) * 100);
}

export function buildContactAddedInsightDescription(totalContacts) {
  const n = safeNum(totalContacts);
  if (n === 0) {
    return "No leads enrolled in this campaign yet. Add recipients to activate sequencing and populate delivery metrics.";
  }
  return `${n} lead${n === 1 ? "" : "s"} enrolled. All performance indicators below—delivery, engagement, and replies—reflect this cohort only.`;
}

export function buildSentInsightDescription(data) {
  const leads = safeNum(data?.totalContacts);
  const sent = safeNum(data?.aggregated?.sent?.count);
  const total = safeNum(data?.aggregated?.sent?.total);
  const queued = Math.max(0, total - sent);

  if (total <= 0 && leads <= 0) {
    return "Outbound volume across your sequence. Figures will appear once leads are enrolled and the campaign begins dispatching.";
  }

  const emailWord = total === 1 ? "email" : "emails";
  const parts = [`${sent} of ${total} sequence ${emailWord} sent.`];

  if (leads > 0 && total > 0 && total % leads === 0) {
    const steps = total / leads;
    parts.push(
      `(${leads} lead${leads === 1 ? "" : "s"} × ${steps} step${steps === 1 ? "" : "s"}).`,
    );
  }

  if (queued > 0) {
    parts.push(`${queued} still queued.`);
  } else if (total > 0 && sent >= total) {
    parts.push("Dispatch complete for this cohort.");
  }

  return parts.join(" ");
}

export function buildOpenedInsightDescription(data) {
  const opened = safeNum(data?.aggregated?.opened?.count);
  const openedTotal = safeNum(data?.aggregated?.opened?.total);
  const sent = safeNum(data?.aggregated?.sent?.count);

  if (openedTotal <= 0 && sent <= 0) {
    return "Open tracking begins once messages are delivered. Metrics here reflect how many recipients viewed your outreach.";
  }

  if (openedTotal <= 0) {
    return `Tracked against ${sent} dispatched email${sent === 1 ? "" : "s"}. Opens will surface as recipients interact with your sequence—strong signal for inbox placement and subject-line resonance.`;
  }

  const om = openedTotal === 1 ? "" : "s";
  return `${opened} open${opened === 1 ? "" : "s"} observed across ${openedTotal} monitored send${om}. Use this to gauge attention quality before optimizing creative or cadence.`;
}

export function buildClickedInsightDescription(data) {
  const clicked = safeNum(data?.aggregated?.clicked?.count);
  const clickedTotal = safeNum(data?.aggregated?.clicked?.total);
  const sent = safeNum(data?.aggregated?.sent?.count);

  if (clickedTotal <= 0 && sent <= 0) {
    return "Link clicks indicate intent beyond the inbox. Data appears after sends complete and recipients engage with your CTAs.";
  }

  if (clickedTotal <= 0) {
    return `Benchmarked against ${sent} delivered send${sent === 1 ? "" : "s"}. Clicks validate message-to-offer fit and help prioritize warm follow-up.`;
  }

  const tm = clickedTotal === 1 ? "" : "s";
  return `${clicked} click${clicked === 1 ? "" : "s"} from ${clickedTotal} tracked send${tm}. Highlights prospects moving deeper into your funnel.`;
}

export function buildRespondedInsightDescription(data) {
  const replied = safeNum(data?.aggregated?.responded?.count);
  const repliedTotal = safeNum(data?.aggregated?.responded?.total);
  const sent = safeNum(data?.aggregated?.sent?.count);

  if (repliedTotal <= 0 && sent <= 0) {
    return "Replies are the primary conversion signal for outbound. Counts will populate as prospects engage with your thread.";
  }

  if (repliedTotal <= 0) {
    return `Measured versus ${sent} email${sent === 1 ? "" : "s"} dispatched. Each reply represents a conversation-ready opportunity—prioritize SLA and routing accordingly.`;
  }

  const tm = repliedTotal === 1 ? "" : "s";
  return `${replied} lead${replied === 1 ? "" : "s"} replied across ${repliedTotal} monitored send${tm}. Strongest indicator of pipeline-ready interest—prioritize fast human follow-up.`;
}
